import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Input, Label, Button, Switch, Checkbox,
} from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { CalendarClock, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { getCsrfToken } from '../../lib/api';

interface BusinessHoursEntry {
  open: string;
  close: string;
}

interface OnlineBookingSettings {
  online_booking_enabled: boolean;
  auto_confirm_bookings: boolean;
  send_confirmation_email: boolean;
  min_rental_days: number;
  max_rental_days: number;
  advance_booking_days: number;
  business_hours: Record<string, BusinessHoursEntry>;
  booking_slug: string;
  booking_url: string;
}

const WEEKDAYS: { key: string; label: string }[] = [
  { key: 'monday', label: 'Montag' },
  { key: 'tuesday', label: 'Dienstag' },
  { key: 'wednesday', label: 'Mittwoch' },
  { key: 'thursday', label: 'Donnerstag' },
  { key: 'friday', label: 'Freitag' },
  { key: 'saturday', label: 'Samstag' },
  { key: 'sunday', label: 'Sonntag' },
];

const DEFAULT_HOURS: BusinessHoursEntry = { open: '08:00', close: '18:00' };

interface FormState {
  online_booking_enabled: boolean;
  auto_confirm_bookings: boolean;
  send_confirmation_email: boolean;
  min_rental_days: string;
  max_rental_days: string;
  advance_booking_days: string;
  business_hours: Record<string, BusinessHoursEntry>;
}

const defaultForm: FormState = {
  online_booking_enabled: false,
  auto_confirm_bookings: false,
  send_confirmation_email: true,
  min_rental_days: '1',
  max_rental_days: '90',
  advance_booking_days: '90',
  business_hours: {},
};

export default function OnlineBookingSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(defaultForm);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['online-booking-settings'],
    queryFn: async () => {
      const response = await fetch('/api/settings/online-booking', { credentials: 'include' });
      if (!response.ok) throw new Error('Fehler beim Laden der Einstellungen');
      return response.json() as Promise<OnlineBookingSettings>;
    },
  });

  useEffect(() => {
    if (settings) {
      setForm({
        online_booking_enabled: settings.online_booking_enabled,
        auto_confirm_bookings: settings.auto_confirm_bookings,
        send_confirmation_email: settings.send_confirmation_email,
        min_rental_days: String(settings.min_rental_days),
        max_rental_days: String(settings.max_rental_days),
        advance_booking_days: String(settings.advance_booking_days),
        business_hours: settings.business_hours,
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data: FormState) => {
      const payload = {
        online_booking_enabled: data.online_booking_enabled,
        auto_confirm_bookings: data.auto_confirm_bookings,
        send_confirmation_email: data.send_confirmation_email,
        min_rental_days: parseInt(data.min_rental_days, 10) || 1,
        max_rental_days: parseInt(data.max_rental_days, 10) || 90,
        advance_booking_days: parseInt(data.advance_booking_days, 10) || 90,
        business_hours: data.business_hours,
      };
      const response = await fetch('/api/settings/online-booking', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Speichern fehlgeschlagen');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['online-booking-settings'] });
      toast({ title: 'Einstellungen gespeichert' });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: 'Fehler', description: error.message });
    },
  });

  function toggleDay(key: string, enabled: boolean) {
    setForm((prev) => {
      const hours = { ...prev.business_hours };
      if (enabled) {
        hours[key] = hours[key] || { ...DEFAULT_HOURS };
      } else {
        delete hours[key];
      }
      return { ...prev, business_hours: hours };
    });
  }

  function updateDayTime(key: string, field: keyof BusinessHoursEntry, value: string) {
    setForm((prev) => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [key]: { ...(prev.business_hours[key] || DEFAULT_HOURS), [field]: value },
      },
    }));
  }

  function copyLink() {
    if (!settings?.booking_url) return;
    navigator.clipboard.writeText(settings.booking_url);
    toast({ title: 'Link kopiert' });
  }

  if (isLoading) {
    return (
      <Card variant="glass">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Online-Buchung
          </CardTitle>
          <CardDescription>
            Kunden können über eine öffentliche Seite selbst einen Mietwagen anfragen —
            sie wählen dabei nur eine Fahrzeugkategorie, kein konkretes Fahrzeug.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <label className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-2.5">
            <span className="text-sm">
              Online-Anfragen aktiv
              <span className="block text-xs text-muted-foreground">
                Ohne Aktivierung liefert die Buchungsseite eine „nicht verfügbar“-Meldung.
              </span>
            </span>
            <Switch
              checked={form.online_booking_enabled}
              onCheckedChange={(value) => setForm({ ...form, online_booking_enabled: value })}
              aria-label="Online-Anfragen aktiv"
            />
          </label>

          {settings?.booking_url && (
            <div className="rounded-md border border-border px-3 py-2.5 flex items-center gap-2">
              <span className="text-sm font-mono truncate flex-1">{settings.booking_url}</span>
              <Button type="button" variant="outline" size="sm" onClick={copyLink}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button type="button" variant="outline" size="sm" asChild>
                <a href={settings.booking_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            </div>
          )}

          <label className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-2.5">
            <span className="text-sm">
              Anfragen automatisch bestätigen
              <span className="block text-xs text-muted-foreground">
                Ohne automatische Bestätigung bleiben neue Anfragen als „Anfrage“
                stehen, bis das Personal sie prüft und ein Fahrzeug zuweist.
              </span>
            </span>
            <Switch
              checked={form.auto_confirm_bookings}
              onCheckedChange={(value) => setForm({ ...form, auto_confirm_bookings: value })}
              aria-label="Anfragen automatisch bestätigen"
            />
          </label>

          <label className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-2.5">
            <span className="text-sm">
              Bestätigungsmail an Kunden senden
            </span>
            <Switch
              checked={form.send_confirmation_email}
              onCheckedChange={(value) => setForm({ ...form, send_confirmation_email: value })}
              aria-label="Bestätigungsmail an Kunden senden"
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="min_rental_days">Mindestmietdauer (Tage)</Label>
              <Input
                id="min_rental_days" type="number" min={1} className="mt-1.5"
                value={form.min_rental_days}
                onChange={(e) => setForm({ ...form, min_rental_days: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="max_rental_days">Maximale Mietdauer (Tage)</Label>
              <Input
                id="max_rental_days" type="number" min={1} className="mt-1.5"
                value={form.max_rental_days}
                onChange={(e) => setForm({ ...form, max_rental_days: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="advance_booking_days">Vorlaufzeit (Tage)</Label>
              <Input
                id="advance_booking_days" type="number" min={1} className="mt-1.5"
                value={form.advance_booking_days}
                onChange={(e) => setForm({ ...form, advance_booking_days: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Öffnungszeiten</Label>
            <div className="mt-2 space-y-2">
              {WEEKDAYS.map(({ key, label }) => {
                const entry = form.business_hours[key];
                const enabled = Boolean(entry);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <Checkbox
                      id={`day-${key}`} checked={enabled}
                      onCheckedChange={(value) => toggleDay(key, value === true)}
                    />
                    <Label htmlFor={`day-${key}`} className="w-24 font-normal">{label}</Label>
                    <Input
                      type="time" disabled={!enabled} className="w-32"
                      value={entry?.open || DEFAULT_HOURS.open}
                      onChange={(e) => updateDayTime(key, 'open', e.target.value)}
                    />
                    <span className="text-muted-foreground text-sm">bis</span>
                    <Input
                      type="time" disabled={!enabled} className="w-32"
                      value={entry?.close || DEFAULT_HOURS.close}
                      onChange={(e) => updateDayTime(key, 'close', e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button" disabled={updateMutation.isPending}
              onClick={() => updateMutation.mutate(form)}
            >
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
